/**
 * **The only place in this slice that knows a generated DTO exists.** Every field is narrowed here
 * — nullable to nullable-with-a-meaning, wire enum to the app's own word — and `ui/` may not import
 * `generated/*` at all, not even type-only.
 *
 * The rule earns its keep on this screen more than most: almost every field on every customer DTO
 * is optional, because orval emits the OpenAPI document faithfully and the document marks nothing
 * required. A component reading `detail.assignedPlan?.weeksTotal ?? 0` in three places is three
 * chances to decide differently about the same absence.
 *
 * ── Three of these reads answer 200 with a literal JSON `null`, and the document does not say so ─
 * `GET /customer/{id}/note`, `/goal` and `/last-session` all return `new JsonResult(null)` rather
 * than a 204. That is deliberate and `CustomerController.cs` gives the reason in as many words: a
 * 204 maps to `undefined` in the client, which would force a special-case fetch path. What it does
 * NOT do is describe the null in the OpenAPI document, so orval types all three as non-nullable —
 * and `dto.body` on a client who has never been written a note is a TypeError that takes the WHOLE
 * screen down rather than one card. Most clients on a fresh account are in exactly that state.
 *
 * So every mapper below guards with `== null`, never `=== undefined`: one covers a read that has
 * not landed AND a body that arrived as null, and the two are the same thing to this screen. The
 * signatures say `| null` for the same reason — the lie belongs at the seam, where it is read
 * beside the endpoint it came from, rather than in a stack trace.
 */

import {
  occurrenceKindOf,
  occurrencePlacement,
  occurrenceStatusOf,
} from '~/entities/calendar-event';
import { schedulingModeIs } from '~/entities/plan-draft';
import type {
  TrainWithMeModulesClientsDtosResponsesCustomerBodyHistoryResponse as BodyHistoryDto,
  TrainWithMeModulesClientsDtosResponsesCustomerBodyResponse as BodyDto,
  TrainWithMeModulesClientsDtosResponsesCustomerConsistencyResponse as ConsistencyDto,
  TrainWithMeModulesClientsDtosResponsesCustomerDetailResponse as DetailDto,
  TrainWithMeModulesClientsDtosResponsesCustomerGoalResponse as GoalDto,
  TrainWithMeModulesClientsDtosResponsesGetCustomerResponse as ProfileDto,
  TrainWithMeModulesClientsDtosResponsesCustomerMuscleVolumeResponse as MuscleVolumeDto,
  TrainWithMeModulesClientsDtosResponsesCustomerNoteResponse as NoteDto,
  TrainWithMeModulesClientsDtosResponsesCustomerPersonalRecordsResponse as RecordsDto,
  TrainWithMeModulesClientsDtosResponsesCustomerProgressSummaryResponse as ProgressSummaryDto,
  TrainWithMeModulesClientsDtosResponsesCustomerRecentSessionsResponse as RecentSessionsDto,
  TrainWithMeModulesClientsDtosResponsesCustomerTrainingTrendsResponse as TrendsDto,
  TrainWithMeModulesClientsDtosResponsesCustomerExerciseMatrixResponse,
  TrainWithMeModulesClientsDtosResponsesCustomerTrainedExercisesResponse,
  TrainWithMeModulesClientsDtosResponsesExerciseMatrixTopSet as MatrixCellDto,
  TrainWithMeModulesTrainingDtosResponsesCalendarEventResponse as CalendarEventDto,
  TrainWithMeModulesNutritionDtosResponsesNutritionAmountsResponse as NutritionAmountsDto,
  TrainWithMeModulesNutritionDtosResponsesNutritionCalculatorInputsResponse as NutritionCalculatorInputsDto,
  TrainWithMeModulesNutritionDtosResponsesNutritionDayResponse as NutritionDayDto,
  TrainWithMeModulesNutritionDtosResponsesNutritionEntryResponse as NutritionEntryDto,
  TrainWithMeModulesNutritionDtosResponsesNutritionMealResponse as NutritionMealDto,
  TrainWithMeModulesNutritionDtosResponsesNutritionPeriodSummaryResponse as NutritionSummaryDto,
  TrainWithMeModulesNutritionDtosResponsesNutritionRangeDay as NutritionRangeDayDto,
  TrainWithMeModulesNutritionDtosResponsesNutritionRangeResponse as NutritionRangeDto,
  TrainWithMeModulesNutritionDtosResponsesNutritionTargetResponse as NutritionTargetResponseDto,
  TrainWithMeModulesNutritionDtosResponsesNutritionTargetValues as NutritionTargetValuesDto,
  TrainWithMeModulesNutritionDtosResponsesNutritionVerdictsResponse as NutritionVerdictsDto,
} from '~/shared/api/generated/model';
import { civilDayInstant, parseInstant } from '~/shared/lib/calendarDate';
import { parseMeasurementFields } from '~/shared/measurement';

import type { AttendanceDay, AttendanceState } from '../model/attendance';
import type { UpcomingSource } from '../model/creditBand';
import type { TrainedExerciseSource } from '../model/formaExercises';
import type { MatrixSource } from '../model/formaMatrix';
import type { ActivityLevel, CalculatorProfile } from '../model/nutritionCalculator';
import { isTrainerSource, type MeasurementSnapshot } from '../model/measurements';
import {
  EMPTY_AMOUNTS,
  type Nutrient,
  type NutritionAmounts,
  type NutritionDay,
  type NutritionDayMeals,
  type NutritionEntry,
  type NutritionMeal,
  type NutritionRange,
  type NutritionStatus,
  type NutritionSummary,
  type NutritionTargetValues,
  type NutritionVerdict,
  type NutritionVerdicts,
} from '../model/nutrition';
import type { MuscleGroupSource } from '../model/muscleVolume';
import type {
  ProgressSummary,
  RecentSessionSource,
  ScheduleOccurrence,
} from '../model/sessionTable';
import type { RecordsSources } from '../model/records';
import type { TrendsSource, WellbeingReadingSource } from '../model/trends';
import type { DetailSource, GoalSource, NoteSource } from '../model/summaryColumn';

export function toDetailSource(dto: DetailDto | undefined): DetailSource | null {
  if (dto?.id == null) return null;
  const plan = dto.assignedPlan ?? null;
  return {
    id: dto.id,
    // All three come off one nested person as of the 25 August 2026 contract. Nothing is derived
    // here any more: the backend composes the name, draws the initials from it and resolves the
    // photo, so the disc reads the same two letters as every other surface showing this client.
    fullName: dto.person?.fullName ?? '',
    initials: dto.person?.initials ?? '',
    avatarUrl: dto.person?.avatarUrl ?? null,
    status: dto.status === 'Invited' ? 'Invited' : dto.status === 'Active' ? 'Active' : null,
    sinceDate: dto.sinceDate ?? null,
    plan:
      plan === null
        ? null
        : {
            planTemplateId: plan.id ?? null,
            assignmentId: plan.assignmentId ?? null,
            name: plan.name ?? '',
            // What the two counts below are COUNTED IN — the contract's own words. A self-paced
            // plan has no weeks to span, so the backend counts its cycles and puts them in the same
            // two fields; the card has to name the right unit or it states a week count that does
            // not exist. Exact match, `null` for a word this build cannot name, per the house rule
            // for every wire enum.
            selfPaced: schedulingModeIs(plan.schedulingMode, 'selfpaced'),
            weeksDone: plan.weeksDone ?? 0,
            weeksTotal: plan.weeksTotal ?? 0,
            endsInDays: plan.endsInDays ?? null,
            paused: plan.paused === true,
            coverImageUrl: plan.coverImageUrl ?? null,
            category: plan.category ?? null,
          },
  };
}

export function toNoteSource(dto: NoteDto | null | undefined): NoteSource | null {
  if (dto == null) return null;
  return { body: dto.body ?? '', updatedText: dto.updatedText ?? null };
}

const CONSISTENCY_STATES: Readonly<Record<string, AttendanceState>> = {
  Trained: 'Trained',
  Missed: 'Missed',
  None: 'None',
};

export function toAttendanceDays(dto: ConsistencyDto | undefined): readonly AttendanceDay[] | null {
  if (dto?.days == null) return null;
  return dto.days.flatMap((day) => {
    if (day.date == null) return [];
    // An unrecognised state counts as nothing scheduled. Guessing `Missed` would blame the client
    // for a status this build does not understand — and `Object.hasOwn` comes first because a wire
    // value of `constructor` would otherwise resolve through `Object.prototype` and survive the
    // `??`, arriving as a function where an `AttendanceState` is expected. See `entities/client`.
    const wire = day.state ?? '';
    const state = Object.hasOwn(CONSISTENCY_STATES, wire) ? CONSISTENCY_STATES[wire] : undefined;
    return [{ date: day.date, state: state ?? 'None' }];
  });
}

/** The first of these that carries a word. `''` is as absent as `null` — see `toGoalSource`. */
function firstWording(...candidates: (string | null | undefined)[]): string {
  return candidates.find((value) => (value ?? '').trim().length > 0)?.trim() ?? '';
}

export function toGoalSource(dto: GoalDto | null | undefined): GoalSource | null {
  if (dto?.id == null) return null;
  return {
    id: dto.id,
    /**
     * Three candidates, because the contract puts the goal's wording in a different field depending
     * on its TYPE and the card has to name it whichever one that is.
     *
     * `Title` is "required for a Custom goal, optional for the measurable types" in the DTO's own
     * words, so a `WeightTarget` or `SessionCount` goal routinely has none — and neither has an
     * exercise to fall back to. What those carry is `Description`: the QA backend answers "Redukcja
     * do 72 kg" for client 2 and "24 treningi w kwartale" for client 3, both with a null title and a
     * null exercise. The card was drawing its heading, its bar and its three figures around an
     * EMPTY string, so a real goal read as an unnamed one.
     *
     * `Description` is `string.Empty` rather than null when blank, which is why this is a
     * first-non-blank rather than a `??` chain — `?? ` would happily settle on `''`.
     */
    title: firstWording(dto.title, dto.exerciseName, dto.description),
    // Carried because `pct` means a different thing per type — a metric for three of them and the
    // elapsed fraction of the deadline for `Custom` — and the card cannot read a 0 correctly
    // without knowing which. See `measurementGapOf`.
    type: dto.type ?? null,
    status: dto.status ?? null,
    deadline: dto.deadline ?? null,
    daysToDeadline: dto.daysToDeadline ?? null,
    targetValue: dto.targetValue ?? null,
    targetUnit: dto.targetUnit ?? null,
    startValue: dto.startValueSnapshot ?? null,
    currentValue: dto.currentValue ?? null,
    pct: dto.pct ?? null,
  };
}

export function toRecentSessions(
  dto: RecentSessionsDto | undefined,
): readonly RecentSessionSource[] | null {
  if (dto?.sessions == null) return null;
  return dto.sessions.flatMap((session) => {
    if (session.workoutExecutionId == null || session.date == null) return [];
    return [
      {
        workoutExecutionId: session.workoutExecutionId,
        date: session.date,
        setsDone: session.setsDone ?? 0,
        tonnageKg: session.tonnage ?? null,
        durationMin: session.durationMin ?? null,
        reviewed: session.reviewed === true,
        isPersonalRecord: session.isPersonalRecord === true,
        setsTotal: session.setsTotal ?? 0,
        // The plan label the backend already resolves (`SessionSummaryFacts.PlanLabel`). It is the
        // session's NAME — "Push Session" — where the wire's `label` is only its date and time.
        planLabel: session.plan ?? null,
        mood: session.mood ?? null,
        // Hours, self-reported for the night before (`CustomerLastSessionResponse.Sleep`).
        sleepHours: session.sleep ?? null,
        clientNote: session.clientNote ?? null,
        // Exact match, `null` for a word this build cannot name — the house rule for a wire enum.
        mode: session.mode === 'WithTrainer' ? 'withTrainer' : session.mode === 'Solo' ? 'solo' : null,
      },
    ];
  });
}

/** The client's schedule, as far as the session table reads it: placement, status and name. */
export function toScheduleOccurrences(
  dto: readonly CalendarEventDto[] | undefined,
): readonly ScheduleOccurrence[] | null {
  if (dto == null) return null;
  return dto.map((event) => ({
    displayDate: event.displayDate,
    eventDate: event.eventDate,
    eventInstant: event.eventInstant,
    moment: event.moment,
    status: event.status,
    derivedStatus: event.derivedStatus ?? null,
    sessionName: event.sessionName ?? null,
  }));
}

/**
 * The „Ostatnie sesje" heading's aside: how many sessions and records the last window held.
 *
 * `null` for an answer carrying neither count, so the aside says nothing rather than „0 sesji".
 */
export function toProgressSummary(dto: ProgressSummaryDto | undefined): ProgressSummary | null {
  if (dto?.sessions == null || dto.personalRecords == null) return null;
  return { sessions: dto.sessions, personalRecords: dto.personalRecords };
}

export function toTrendsSource(dto: TrendsDto | undefined): TrendsSource | null {
  if (dto?.weeks == null) return null;
  return {
    weeks: dto.weeks.flatMap((week) => {
      if (week.weekStart == null) return [];
      return [
        {
          weekStart: week.weekStart,
          label: week.weekLabel ?? '',
          tonnageKg: week.tonnageKg ?? 0,
          // Both averages are deliberately kept nullable all the way to the chart. The contract
          // sends `null` for a week nobody answered precisely so it does not read as zero hours
          // slept or as a client who felt nothing.
          avgSleepHours: week.avgSleepHours ?? null,
          avgMood: week.avgMood ?? null,
        },
      ];
    }),
    // The individual readings behind those averages, which the two wellbeing cards offer as their
    // other scale. Absent on an older backend rather than empty, and an empty list is what a client
    // who logged nothing sends — the card cannot tell the two apart and does not need to, because
    // both mean "there is nothing to draw here".
    sleepReadings: toWellbeingReadings(dto.sleepReadings, (reading) => reading.hours),
    moodReadings: toWellbeingReadings(dto.moodReadings, (reading) => reading.value),
  };
}

/**
 * One wellbeing list, mapped. A reading with no instant is DROPPED rather than defaulted: an
 * undated point cannot be placed on a time axis, and placing it at the epoch would drag the shared
 * x domain back to 1970 and flatten all three charts into the last pixel.
 */
function toWellbeingReadings<T extends { at?: string }>(
  readings: readonly T[] | null | undefined,
  value: (reading: T) => number | undefined,
): readonly WellbeingReadingSource[] {
  return (readings ?? []).flatMap((reading) => {
    const at = reading.at;
    const measured = value(reading);
    if (at == null || measured == null) return [];
    return [{ at, value: measured }];
  });
}

export function toMuscleGroups(
  dto: MuscleVolumeDto | undefined,
): readonly MuscleGroupSource[] | null {
  if (dto?.groups == null) return null;
  return dto.groups.flatMap((group) => {
    if (group.muscleGroup == null) return [];
    return [
      {
        muscleGroup: group.muscleGroup,
        cluster: group.cluster ?? 'Other',
        weeklySets: group.weeklySets ?? 0,
      },
    ];
  });
}

/**
 * The client's sex, as the contract's own word.
 *
 * The DETAIL read does not carry it — `CustomerDetailResponse` is identity, status, since-date and
 * the assigned plan — so the silhouette's sex comes from the profile read, which is the cheapest
 * place it exists (`GET /customer/{id}`, one client). Passed on as the string the wire uses rather
 * than as a decision: `model/muscleVolume`'s `silhouetteGender` is where three contract values and a
 * null become one of two drawings.
 */
export function toClientGender(dto: ProfileDto | undefined): string | null {
  return dto?.gender ?? null;
}

export function toMeasurementSnapshots(
  latest: BodyDto | null | undefined,
  history: BodyHistoryDto | null | undefined,
): readonly MeasurementSnapshot[] | null {
  const items = history?.items;
  if (items == null && latest == null) return null;
  const rows = items ?? (latest?.latest == null ? [] : [latest.latest]);
  return rows.flatMap((item) => {
    if (item.recordedAt == null || item.weightKg == null) return [];
    return [
      {
        id: item.id ?? 0,
        recordedAt: item.recordedAt,
        // Restored 19 September 2026: the canon draws a trainer's reading and a client's check-in
        // as two different marks and names which one the readout is standing on.
        byTrainer: isTrainerSource(item.source),
        weightKg: item.weightKg,
        bodyFatPercentage: item.bodyFatPercentage ?? null,
        waistCm: item.waistCm ?? null,
        hipCm: item.hipCm ?? null,
        chestCm: item.chestCm ?? null,
        neckCm: item.neckCm ?? null,
        bicepCm: item.bicepCm ?? null,
        thighCm: item.thighCm ?? null,
        calfCm: item.calfCm ?? null,
        shoulderCm: item.shoulderCm ?? null,
        forearmCm: item.forearmCm ?? null,
      },
    ];
  });
}

export function toRecordsSources(dto: RecordsDto | null | undefined): RecordsSources | null {
  if (dto == null) return null;
  return {
    big3: (dto.big3 ?? []).flatMap((lift) => {
      if (lift.lift == null) return [];
      return [
        {
          lift: lift.lift,
          exerciseName: lift.exerciseName ?? null,
          bestWeightKg: lift.bestWeightKg ?? null,
          // The UTC instant the best set was performed. `achievedAtLabel` is beside it on the wire
          // and carries the same fact as a relative phrase; the cards print the DATE, so the label
          // is not read at all. See `model/records`.
          achievedAt: lift.achievedAt ?? null,
          targetWeightKg: lift.targetWeightKg ?? null,
          history: (lift.weightHistory ?? []).flatMap((point) =>
            point.achievedAt == null || point.weightKg == null
              ? []
              : [{ achievedAt: point.achievedAt, weightKg: point.weightKg }],
          ),
        },
      ];
    }),
    recent: (dto.recent ?? []).flatMap((entry) => {
      if (entry.achievedAt == null) return [];
      return [
        {
          exerciseId: entry.exerciseId ?? null,
          exerciseName: entry.exerciseName ?? '',
          prKind: entry.prKind ?? 'E1rm',
          valueKg: entry.valueKg ?? null,
          e1rmKg: entry.e1rmKg ?? null,
          reps: entry.reps ?? null,
          achievedAt: entry.achievedAt,
          deltaVsPrevKg: entry.deltaVsPrevKg ?? null,
        },
      ];
    }),
  };
}

type TrainedExercisesDto = TrainWithMeModulesClientsDtosResponsesCustomerTrainedExercisesResponse;
type MatrixDto = TrainWithMeModulesClientsDtosResponsesCustomerExerciseMatrixResponse;

export function toTrainedExercises(dto: TrainedExercisesDto | undefined): readonly TrainedExerciseSource[] {
  return (dto?.exercises ?? []).flatMap((exercise) =>
    exercise.exerciseId === undefined
      ? []
      : [
          {
            exerciseId: exercise.exerciseId,
            name: exercise.name ?? '',
            clipCount: exercise.clipCount ?? 0,
            lastClipAt: parseInstant(exercise.lastClipAt),
            lastLoggedAt: parseInstant(exercise.lastLoggedAt),
            unread: exercise.unread === true,
          },
        ],
  );
}

/** A cell may arrive as `null` although the generated type says it cannot: the week not lifted. */
export function toMatrixSource(dto: MatrixDto | undefined): MatrixSource {
  const cells = (row: { cells: readonly (MatrixCellDto | null)[] | null }) =>
    (row.cells ?? []).map((cell) =>
      cell === null
        ? null
        : {
            weightKg: cell.weightKg ?? null,
            reps: cell.reps ?? null,
            e1rmKg: cell.e1rmKg ?? null,
            durationSeconds: cell.durationSeconds ?? null,
            distanceMeters: cell.distanceMeters ?? null,
            rounds: cell.rounds ?? null,
            speedKmh: cell.speedKmh ?? null,
          },
    );
  return {
    weeks: (dto?.weeks ?? []).flatMap((week) => {
      const monday = week.startsOn === undefined ? null : civilDayInstant(week.startsOn);
      return monday === null ? [] : [monday];
    }),
    rows: (dto?.rows ?? []).flatMap((row) =>
      row.exerciseId === undefined
        ? []
        : [
            {
              exerciseId: row.exerciseId,
              name: row.name ?? '',
              fields: row.measurementFields == null ? null : parseMeasurementFields(row.measurementFields),
              cells: cells(row),
              change:
                row.primaryChange4w?.metric == null || row.primaryChange4w.value === undefined
                  ? null
                  : { metric: row.primaryChange4w.metric, value: row.primaryChange4w.value },
              e1rmChange4wKg: row.e1rmChange4wKg ?? null,
            },
          ],
    ),
    truncated: dto?.truncated === true,
  };
}

/**
 * One occurrence on the client's calendar, as the „Rozliczenie" band's „Nadchodzące" reads it —
 * placed on the reader's own clock (R17, `entities/calendar-event`). What kind it is and how that
 * is said is the entity's (`occurrenceKindKey`), decided in the band's model.
 */
export function toUpcomingSource(event: CalendarEventDto): UpcomingSource {
  const placed = occurrencePlacement(event, null);
  return {
    id: event.id,
    startMs: placed?.atMs ?? (placed === null ? null : civilDayInstant(placed.day)),
    hasHour: placed?.atMs != null,
    status: occurrenceStatusOf({
      status: event.status,
      derivedStatus: event.derivedStatus ?? null,
    }),
    kind: occurrenceKindOf(event.type),
    planSessionName: event.sessionName?.trim() || null,
    minutes: event.effectiveDurationMinutes > 0 ? event.effectiveDurationMinutes : null,
  };
}

/* ── Odżywianie (nutrition stage 1) ─────────────────────────────────────────────────────────────
 * The wire's nutrient names are the backend's enum words; the tab's are its own. A status or a
 * nutrient this build has never heard of is dropped rather than guessed: an unknown status reads as
 * „no target" (nothing judged), an unknown reason is not printed. */

const NUTRITION_STATUS: Readonly<Record<string, NutritionStatus>> = {
  OnTarget: 'onTarget',
  OffTarget: 'offTarget',
  NotLogged: 'notLogged',
  InProgress: 'inProgress',
  NoTarget: 'noTarget',
};

const NUTRITION_VERDICT: Readonly<Record<string, NutritionVerdict>> = {
  OnTarget: 'ok',
  OffTarget: 'warn',
  InProgress: 'today',
  NoTarget: 'plain',
  NotLogged: 'none',
  NotGiven: 'na',
};

/** An unknown verdict reads as „no target": drawn, never judged. */
function toNutritionVerdicts(dto: NutritionVerdictsDto): NutritionVerdicts {
  const of = (wire: string): NutritionVerdict => NUTRITION_VERDICT[wire] ?? 'plain';
  return { energy: of(dto.energy), protein: of(dto.protein), carbs: of(dto.carbs), fat: of(dto.fat) };
}

const NUTRIENT_OF: Readonly<Record<string, Nutrient>> = {
  Energy: 'energy',
  Protein: 'protein',
  Carbs: 'carbs',
  Fat: 'fat',
};

function toNutritionAmounts(dto: NutritionAmountsDto | null | undefined): NutritionAmounts {
  if (dto === null || dto === undefined) return EMPTY_AMOUNTS;
  return {
    energy: dto.energyKcal ?? null,
    protein: dto.proteinG ?? null,
    carbs: dto.carbsG ?? null,
    fat: dto.fatG ?? null,
    sugar: dto.sugarG ?? null,
  };
}

function toNutritionTargetValues(
  dto: NutritionTargetValuesDto | null | undefined,
): NutritionTargetValues | null {
  if (dto === null || dto === undefined) return null;
  return {
    effectiveFrom: dto.effectiveFrom,
    energy: dto.energyKcal,
    protein: dto.proteinG ?? null,
    carbs: dto.carbsG ?? null,
    fat: dto.fatG ?? null,
  };
}

export function toNutritionTarget(dto: NutritionTargetResponseDto): NutritionTargetValues | null {
  return toNutritionTargetValues(dto.target);
}

const ACTIVITY_LEVEL: Readonly<Record<string, ActivityLevel>> = {
  Sedentary: 'sedentary',
  Light: 'light',
  Moderate: 'moderate',
  High: 'high',
};

export function toCalculatorProfile(dto: NutritionCalculatorInputsDto): CalculatorProfile {
  const weight = dto.weight ?? null;
  return {
    sex: dto.sex === 'Female' ? 'female' : dto.sex === 'Male' ? 'male' : dto.sex === 'Other' ? 'other' : null,
    dateOfBirth: dto.dateOfBirth ?? null,
    ageYears: dto.ageYears ?? null,
    heightCm: dto.heightCm ?? null,
    weight:
      weight === null
        ? null
        : {
            kg: weight.kg,
            bodyFatPercent: weight.bodyFatPercent ?? null,
            measuredAt: weight.measuredAt,
            isStale: weight.isStale,
          },
    activity: dto.activityLevel == null ? null : (ACTIVITY_LEVEL[dto.activityLevel] ?? null),
  };
}

function toNutritionSummary(dto: NutritionSummaryDto): NutritionSummary {
  return {
    loggedDays: dto.loggedDays,
    judgedDays: dto.judgedDays,
    onTargetDays: dto.onTargetDays,
    averages: toNutritionAmounts(dto.averages),
  };
}

function toNutritionDay(dto: NutritionRangeDayDto): NutritionDay {
  return {
    date: dto.date,
    isToday: dto.isToday,
    target: toNutritionTargetValues(dto.target),
    totals: toNutritionAmounts(dto.totals),
    partial: {
      protein: dto.missing.protein,
      carbs: dto.missing.carbs,
      fat: dto.missing.fat,
      sugar: dto.missing.sugar,
    },
    status: NUTRITION_STATUS[dto.status] ?? 'noTarget',
    reasons: dto.reasons.flatMap((miss) => {
      const nutrient = NUTRIENT_OF[miss.nutrient];
      return nutrient === undefined ? [] : [{ nutrient, difference: miss.difference }];
    }),
    verdicts: toNutritionVerdicts(dto.verdicts),
    weightKg: dto.weightKg ?? null,
    mealCount: dto.mealCount,
  };
}

export function toNutritionRange(dto: NutritionRangeDto): NutritionRange {
  return {
    from: dto.from,
    to: dto.to,
    days: dto.days.map(toNutritionDay),
    summary: toNutritionSummary(dto.summary),
    weeks:
      dto.weeks === null || dto.weeks === undefined
        ? null
        : dto.weeks.map((week) => ({
            weekStart: week.weekStart,
            weekEnd: week.weekEnd,
            summary: toNutritionSummary(week.summary),
            averagesWithToday: toNutritionAmounts(week.averagesWithToday),
            target: toNutritionTargetValues(week.target),
            verdicts: toNutritionVerdicts(week.verdicts),
            weightKg: week.weightKg ?? null,
          })),
    lastEntryAt: dto.lastEntryAt ?? null,
  };
}

const ENTRY_KIND: Readonly<Record<string, NutritionEntry['kind']>> = {
  Product: 'product',
  Manual: 'manual',
  DayTotal: 'dayTotal',
};

function toNutritionEntry(entry: NutritionEntryDto): NutritionEntry {
  return {
    key: entry.entryKey,
    kind: ENTRY_KIND[entry.kind] ?? 'other',
    name: entry.product?.name?.trim() || entry.name?.trim() || null,
    amounts: toNutritionAmounts(entry),
    product: entry.product
      ? { brand: entry.product.brand?.trim() || null, grams: entry.grams ?? null, sourceUrl: entry.product.sourceUrl }
      : null,
  };
}

function toNutritionMeal(meal: NutritionMealDto): NutritionMeal {
  return {
    key: meal.mealKey,
    name: meal.name?.trim() || null,
    startedAt: meal.startedAt ?? null,
    items: meal.items.map(toNutritionEntry),
    amounts: toNutritionAmounts(meal.totals),
  };
}

/** The meals in the server's order (started at, then creation), each item in write order. */
export function toNutritionDayMeals(dto: Pick<NutritionDayDto, 'meals' | 'dayTotal'>): NutritionDayMeals {
  return {
    meals: dto.meals.map(toNutritionMeal),
    dayTotal: dto.dayTotal === null || dto.dayTotal === undefined ? null : toNutritionEntry(dto.dayTotal),
  };
}
